import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  MenuItem,
  FormHelperText,
  Select,
  FormControl,
  Snackbar,
  Alert,
  InputLabel,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import styles from "./Account.module.css"; // Custom CSS for additional styling
import CustomInput from "../../../shared/utils/Input";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import GlobalSnackbar from "../../../shared/utils/GlobalSnackbar";
import axios from "axios";
import GlobalButton from "../../../shared/utils/GlobalButton";

const ProfileForm = ({ onProfileData }) => {
  const userRole = localStorage.getItem("userRole");
  const [formData, setFormData] = useState({
    name: "",
    email: localStorage.getItem("email") || "",
    role: "",
    phoneNumber: "",
    yourTotalCampaigns: "",
    companyCampaigns: "",
  });

  const [errors, setErrors] = useState({});
  const [nextButtonClicked, setNextButtonClicked] = useState(false);
  const [isEditable, setIsEditable] = useState(false); // State to track edit mode
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const serverURL = process.env.REACT_APP_URL;

  // Fetch data when the component mounts
  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${serverURL}/client/dashboard/${localStorage.getItem("clientId")}`,
      );
      const { data, campaignCount, TotalCampaignCount } = response.data;
      setFormData((prevFormData) => ({
        ...prevFormData,
        name: data?.name || "", // Default to empty string if name is undefined
        email: data?.emailAddress || "", // Handle potential undefined emailAddress
        phoneNumber: data?.phoneNumber ? data.phoneNumber.toString() : "", // Only convert to string if phoneNumber is defined
        role: data?.roleInOrganization || "", // Default to empty string if undefined
        companyCampaigns: TotalCampaignCount,
        yourTotalCampaigns: campaignCount,
      }));
      localStorage.setItem(
        "organisationId",
        response?.data?.organizationData?.organizationId?._id,
      );
      if (onProfileData)
        onProfileData(
          response?.data?.organizationData?.organizationId?.brandLogo,
        );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate the input and remove the error if it's valid
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      // Name validation
      if (name === "name" && value) {
        const isValidUserName = /^[a-zA-Z\s]+$/.test(value);

        if (isValidUserName) {
          delete newErrors.name;
        } else {
          newErrors.userName =
            "Username should not contain numbers or special characters.";
        }
      }

      // Role validation
      if (name === "role" && value) {
        delete newErrors.role;
      }

      // Phone number validation
      if (
        name === "phoneNumber" &&
        /^[0-9]{10}$/.test(value) &&
        !/[eE.]/.test(value)
      ) {
        delete newErrors.phoneNumber; // Remove error if the phone number is valid
      }
      if (name === "yourTotalCampaigns" && value) {
        delete newErrors.yourTotalCampaigns;
      }
      if (name === "companyCampaigns" && value) {
        delete newErrors.companyCampaigns;
      }
      return newErrors;
    });
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) {
      newErrors.name = "Name is required.";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = "Name should only contain letters and spaces.";
    }

    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
      newErrors.email = "Invalid email format.";
    }

    if (!formData.role) {
      newErrors.role = "Role is required.";
    }

    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required.";
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number should be 10 digits.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // No errors
  };

  // handle save button
  const handleSave = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Extract the necessary fields from formData
        setIsLoading(true);
        const {
          name,
          email,
          phoneNumber,
          role,
          yourTotalCampaigns,
          companyCampaigns,
        } = formData;

        // Make the PUT request
        const response = await axios.put(
          `${serverURL}/client/edit/${localStorage.getItem("clientId")}`,
          {
            name,
            emailAddress: email, // Assuming your backend expects this field as 'emailAddress'
            phoneNumber,
            roleInOrganization: role,
            yourTotalCampaigns,
            companyCampaigns,
          },
        );
        if (response.data.success) {
          setSnackbar({
            open: true,
            message: "Profile updated successfully.",
            severity: "success",
          });
          setIsLoading(false);
          fetchData();
          localStorage.setItem("name", response.data.data.name);
          setIsEditable(false);
        } else {
          setSnackbar({
            open: true,
            message: "Error updating profile.",
            severity: "error",
          });
          setIsLoading(false);
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Error updating profile.",
          severity: "error",
        });
        setIsLoading(false);

        console.error("Error submitting the form:", error);
      }
    }
  };

  const handleEdit = () => {
    setIsEditable(!isEditable);
  };
  const handleDiscard = () => {
    setIsEditable(false); // Toggle edit state
    // getOrganizationDetails(clientId);
    setErrors({});
  };

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileHeader}>
        <h2 className={styles.heading}>PROFILE</h2>
        <div className={styles.editButton}>
          {isEditable ? (
            <div className="flex gap-2 justify-center items-center">
              <GlobalButton
                variant="warning"
                size="small"
                leftIcon={<DeleteOutlineIcon />}
                onClick={handleDiscard}
              >
                {`Discard`}
              </GlobalButton>

              <GlobalButton
                variant="primary"
                size="small"
                isLoading={isLoading}
                onClick={handleSave}
              >
                {`Save`}
              </GlobalButton>
            </div>
          ) : (
            <div className={styles.editButton}>
              {userRole == 1 && (
                <GlobalButton
                  variant="tertiary"
                  size="small"
                  leftIcon={<DriveFileRenameOutlineOutlinedIcon />}
                  onClick={handleEdit}
                >
                  {`Edit`}
                </GlobalButton>
              )}
            </div>
          )}
        </div>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <CustomInput
            className="text-xs font-medium leading-6 text-left"
            placeholder="Enter Your Name"
            title="Name *"
            type="text"
            disabled={!isEditable}
            size="small"
            name="name"
            error={errors.name}
            errorMessage={errors.name}
            value={formData.name}
            customcolor="var(--heading-color)"
            fontWeight="500"
            onChange={handleInputChange}
            onBlur={() => {
              if (nextButtonClicked) {
                validateForm();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <CustomInput
            className="text-xs font-medium leading-6 text-left"
            placeholder="Enter Your Email"
            title="Email *"
            type="text"
            disabled
            size="small"
            name="email"
            error={errors.email}
            errorMessage={errors.email}
            value={formData.email}
            customcolor="var(--heading-color)"
            fontWeight="500"
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <div className="mb-4">
            <InputLabel
              error={errors.role}
              style={{
                fontWeight: "400",
                color: errors.role
                  ? "var(--error-color)"
                  : "var(--heading-color)",
              }}
            >
              Role *
            </InputLabel>
            <FormControl
              fullWidth
              size="small"
              disabled={!isEditable}
              sx={{ marginTop: "0.5rem" }}
              error={errors.role}
            >
              <Select
                inputProps={{
                  "aria-label": "Without label",
                }}
                displayEmpty
                name="role"
                value={formData?.role}
                onChange={handleInputChange}
                renderValue={(selected) => {
                  // This renders gray color when no option is selected
                  if (!selected || selected.length === 0) {
                    return (
                      <p className="text-[1rem] text-[var(--listdown-color)]">
                        Select Role
                      </p>
                    );
                  }
                  // When an option is selected, show black
                  return (
                    <span style={{ color: "var(--heading-color)" }}>
                      {selected}
                    </span>
                  );
                }}
                sx={{
                  color: formData?.role
                    ? "var(--heading-color)"
                    : "var(--label-color)", // Default gray, selected text turns black
                }}
              >
                <MenuItem value="Client">Client</MenuItem>
                <MenuItem value="Cheif marketing Officer">
                  Cheif marketing Officer
                </MenuItem>
                <MenuItem value="Marketing Manager">Marketing Manager</MenuItem>
                <MenuItem value="Advertising Specialis">
                  Advertising Specialist
                </MenuItem>
                <MenuItem value="Market Research Analyst">
                  Market Research Analyst
                </MenuItem>
                <MenuItem value="Creative Director">Creative Director</MenuItem>
              </Select>
              {errors.role && <FormHelperText>Select Role</FormHelperText>}
            </FormControl>
          </div>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <CustomInput
            className="text-xs font-medium leading-6 text-left"
            placeholder="Enter Your Phone Number"
            title="Phone Number *"
            type="number"
            disabled={!isEditable}
            size="small"
            name="phoneNumber"
            error={errors.phoneNumber}
            errorMessage={errors.phoneNumber}
            value={formData.phoneNumber}
            customcolor="var(--heading-color)"
            fontWeight="500"
            onChange={(e) => {
              if (
                !e.target.value.startsWith(" ") &&
                e.target.value.length < 11
              ) {
                handleInputChange(e);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <CustomInput
            className="text-xs font-medium leading-6 text-left"
            placeholder="Enter Your Campaigns"
            title="Your Total Campaigns "
            disabled
            type="number"
            size="small"
            name="yourTotalCampaigns"
            value={formData.yourTotalCampaigns}
            customcolor="var(--heading-color)"
            fontWeight="500"
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <CustomInput
            className="text-xs font-medium leading-6 text-left"
            placeholder="Enter Your Companies"
            title="Company Campaigns "
            type="number"
            size="small"
            disabled
            name="companyCampaigns"
            value={formData.companyCampaigns}
            customcolor="var(--heading-color)"
            fontWeight="500"
          />
        </Grid>
      </Grid>
      {/* Snackbar for success or error messages  */}
      <GlobalSnackbar snackbar={snackbar} handleClose={handleSnackbarClose} />
    </div>
  );
};

export default ProfileForm;
